import { PublisherFrameListConfiguration } from 'components/common/types/Publisher.types';
import { AggregatedFrame } from './FramesReport.types';

export type ColumnFormat = 'number' | 'currency' | 'tags' | 'address' | 'boolean';

export interface FrameReportColumn {
  field: string;
  label: string;
  format?: ColumnFormat;
  pinned?: 'left' | 'right';
  optional?: boolean;
}

export const formatAddress = (address?: AggregatedFrame['address']): string => {
  if (!address) return '';

  const { detail1, detail2 } = address;
  if (detail1 && detail2) return `${detail1}, ${detail2}`;

  return detail1 || '';
};

export const formatBooleanAsZeroOne = (value: unknown): 0 | 1 | '' => {
  if (typeof value !== 'boolean') return '';

  return value ? 1 : 0;
};

export const FRAME_REPORT_COLUMNS: FrameReportColumn[] = [
  { field: 'frameId', label: 'Frame ID', pinned: 'left' },
  { field: 'frameCode', label: 'Frame code' },
  { field: 'frameAltCode', label: 'Frame alt code' },
  { field: 'visualUnitCode', label: 'Visual unit code' },
  { field: 'frameSizeFamilyName', label: 'Frame size family name' },
  { field: 'productFormatName', label: 'Product format' },
  { field: 'frameSize', label: 'Frame size' },
  { field: 'furnitureCode', label: 'Furniture code', optional: true },
  { field: 'furnitureIndex', label: 'Furniture index', optional: true },
  { field: 'mediaType', label: 'Media type', optional: true },
  { field: 'illuminated', label: 'Illumination', format: 'boolean', optional: true },
  { field: 'channelName', label: 'Channel', optional: true },
  { field: 'countryName', label: 'Country', optional: true },
  { field: 'businessAreaName', label: 'Business area', optional: true },
  { field: 'revenueStreamName', label: 'Revenue stream', optional: true },
  { field: 'networks', label: 'Network(s)', format: 'tags', optional: true },
  { field: 'address', label: 'Address', format: 'address' },
  { field: 'city', label: 'City' },
  { field: 'district', label: 'District' },
  { field: 'county', label: 'County' },
  { field: 'postCode', label: 'Postcode' },
  { field: 'tvAreaName', label: 'TV area', optional: true },
  { field: 'conurbationName', label: 'Conurbation', optional: true },
  { field: 'routeTownName', label: 'Route town', optional: true },
  { field: 'geographicalAreaLevel1Name', label: 'Geo area level 1', optional: true },
  { field: 'geographicalAreaLevel2Name', label: 'Geo area level 2', optional: true },
  { field: 'geographicalAreaLevel3Name', label: 'Geo area level 3', optional: true },
  { field: 'tags', label: 'Tags', format: 'tags', optional: true },
  { field: 'latitude', label: 'Latitude' },
  { field: 'longitude', label: 'Longitude' },
  { field: 'impression', label: 'Impressions', format: 'number', pinned: 'right' },
  { field: 'price', label: 'Price (Gross)', format: 'currency', pinned: 'right', optional: true },
  { field: 'indexedPrice', label: 'Price (Indexed)', format: 'currency', pinned: 'right', optional: true },
];

export const isIlluminationPresentInConfig = (frameListConfiguration?: PublisherFrameListConfiguration): boolean =>
  Boolean(frameListConfiguration?.metadataFields?.includes('ILLUMINATION'));

export const getVisibleFrameReportColumns = (
  frameListConfiguration?: PublisherFrameListConfiguration,
): FrameReportColumn[] =>
  FRAME_REPORT_COLUMNS.filter(
    (col) => col.field !== 'illuminated' || isIlluminationPresentInConfig(frameListConfiguration),
  );

export const getExportValue = (row: AggregatedFrame, col: FrameReportColumn): string | number => {
  const value = (row as Record<string, unknown>)[col.field];

  switch (col.format) {
    case 'currency':
    case 'number':
      return (value as number) ?? 0;
    case 'tags':
      return Array.isArray(value) ? value.join(', ') : '';
    case 'address':
      return formatAddress(value as AggregatedFrame['address']);
    case 'boolean':
      return formatBooleanAsZeroOne(value);
    default:
      return (value as string | number) ?? '';
  }
};
